import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Building2 } from 'lucide-react';
import { Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import { Badge, Button, Input } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, Pagination } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { useOpsOrgs, type OpsOrgListItem } from '@/api/operations-org-hooks';
import { paginationView } from '@/pages/customers/pagination-summary';
import { TabLoading } from '@/pages/customers/detail/tab-states';
import type { ColumnDef } from '@vnetwork/ui/data';
import { orgCategoryTotal } from '@/api/operations-org-hooks';
import { CATEGORY_LABEL } from '../detail/tabs/ops-detail-shared';
import type { OpsCategory } from '@/api/operations';
import { EntityLink } from '@/components/entity-link';

const PAGE_SIZE = 20;

/** Categories to show as count badges in the services column. */
const BADGE_CATS: OpsCategory[] = ['cdn', 'waap', 'compute', 'storage', 'media'];

/** Debounce a value by delayMs to avoid spamming the backend. */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

const columns: ColumnDef<OpsOrgListItem>[] = [
  {
    id: 'org_id',
    header: 'ID',
    accessorFn: (r) => r.organization_id,
    cell: ({ row }) => {
      const c = row.original.customer;
      return (
        <EntityLink
          to={`/operations/orgs/${row.original.organization_id}`}
          id={row.original.organization_id}
          name={c?.customer_company_name || c?.customer_name}
        />
      );
    },
  },
  {
    id: 'services',
    header: 'Services',
    accessorFn: (r) => r.services.total,
    cell: ({ row }) => {
      const { services } = row.original;
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-1)' }}>
          {BADGE_CATS.map((cat) => {
            const count = orgCategoryTotal(services, cat);
            if (!count) return null;
            return (
              <Badge key={cat} tone="neutral" appearance="subtle" size="sm">
                {CATEGORY_LABEL[cat]} {count}
              </Badge>
            );
          })}
          {!services.total && (
            <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-xs)' }}>
              —
            </span>
          )}
        </div>
      );
    },
  },
  {
    id: 'teams',
    header: 'Teams',
    accessorFn: (r) => r.teams_count,
    cell: ({ row }) => (
      <span style={{ fontSize: 'var(--vn-font-size-sm)' }}>
        {row.original.teams_count.toLocaleString('en-US')}
      </span>
    ),
  },
  {
    id: 'projects',
    header: 'Projects',
    accessorFn: (r) => r.projects_count,
    cell: ({ row }) => (
      <span style={{ fontSize: 'var(--vn-font-size-sm)' }}>
        {row.original.projects_count.toLocaleString('en-US')}
      </span>
    ),
  },
  {
    id: 'domains',
    header: 'Domains',
    accessorFn: (r) => r.domain_count,
    cell: ({ row }) => (
      <span style={{ fontSize: 'var(--vn-font-size-sm)' }}>
        {row.original.domain_count.toLocaleString('en-US')}
      </span>
    ),
  },
];

/**
 * /operations/orgs — paginated org list with search.
 * Layout: PageHeader → Toolbar (search) → DataTable → Pagination.
 * Row click navigates to /operations/orgs/:orgId.
 */
export default function OperationsOrgsPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [rawQuery, setRawQuery] = useState('');
  const query = useDebouncedValue(rawQuery, 400);

  const { data, isLoading, isError, isFetching, refetch } = useOpsOrgs({
    page,
    per_page: PAGE_SIZE,
    q: query || undefined,
  });

  // Reset to page 1 on new search
  useEffect(() => {
    setPage(1);
  }, [query]);

  const rows: OpsOrgListItem[] = (data?.results as OpsOrgListItem[] | undefined) ?? [];
  const pagination = (data as { pagination?: { total?: number; total_pages?: number } } | undefined)
    ?.pagination;
  const total = pagination?.total ?? rows.length;
  const pageCount = pagination?.total_pages ?? 1;

  const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);

  return (
    <Container size="full">
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <PageHeader
        title="Organizations"
        description="All organizations with provisioned services — drill into teams, projects, and service toggles."
      />

      <Toolbar
        style={{ marginBottom: 0 }}
        start={
          <Input
            value={rawQuery}
            onChange={(e) => setRawQuery(e.target.value)}
            placeholder="Search by org ID or customer name…"
            startSlot={<Search size={15} />}
            style={{ maxWidth: 320 }}
          />
        }
      />

      {isError ? (
        <Alert
          tone="danger"
          title="Failed to load organizations"
          actions={
            <Button size="sm" variant="ghost" onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      ) : isLoading ? (
        <TabLoading rows={8} />
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(r) => r.organization_id}
          onRowClick={(row) => navigate(`/operations/orgs/${row.organization_id}`)}
          emptyState={
            <EmptyState
              icon={<Building2 size={24} />}
              title="No organizations found"
              description="Try adjusting your search."
            />
          }
          footer={
            <Pagination
              page={page}
              pageCount={view.pageCount}
              onPageChange={setPage}
              summary={`${view.summary}${isFetching ? ' …' : ''}`}
            />
          }
        />
      )}
      </div>
    </Container>
  );
}
